import { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  Stack,
  Button,
  Chip,
  Alert,
  Divider,
  Paper,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { updateSignup } from '../../../../redux/pricingWizardSlice';
import WizardSection from '../components/WizardSection';
import MasterInfoCard from '../components/MasterInfoCard';
import CreditCard from '../components/CreditCard';
import WarehouseCard from '../components/WarehouseCard';

const FIELDS = [
  { name: 'business_name', label: 'Business Name', required: true, placeholder: 'Registered business name (as per GST/PAN)', icon: BusinessIcon },
  { name: 'brand', label: 'Brand Name', optional: true, placeholder: 'Customer-facing brand name', icon: LocalOfferOutlinedIcon, hint: 'Defaults to Business Name if left blank.' },
  { name: 'email', label: 'Email', required: true, placeholder: 'ops@yourbrand.com', icon: EmailOutlinedIcon, type: 'email' },
  { name: 'contact', label: 'Contact Number', required: true, placeholder: '10-digit mobile', icon: PhoneOutlinedIcon },
];
// HubSpot Deal ID / Sales POC used to be entered here -- moved to the "All Merchants" ->
// merchant view page instead (see MerchantTextFieldEdit / MarketingToggle in
// "pages/merchants/merchant details/details.js"), which is now the only place either field
// (and the new Marketing yes/no flag) is ever set, for both new and existing merchants.

export function validateSignup(signup) {
  const errors = {};
  if (!signup.business_name) errors.business_name = 'Business Name is required.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signup.email)) errors.email = 'A valid email is required.';
  if (!/^\d{10}$/.test(signup.contact)) errors.contact = 'A 10-digit mobile is required.';
  if (!signup.address) errors.address = 'Address is required.';
  if (!/^\d{6}$/.test(signup.pincode)) errors.pincode = 'A 6-digit pincode is required.';
  if (!signup.city) errors.city = 'City is required.';
  if (!signup.state) errors.state = 'State is required.';
  return errors;
}

function InfoField({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: 'primary.lighter', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon sx={{ fontSize: 15, color: 'primary.main' }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', mt: 0.3, lineHeight: 1.35 }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function Stage0_Signup({ errors = {} }) {
  const dispatch = useDispatch();
  const signup = useSelector((s) => s.pricingWizard.signup);
  const merchantId = useSelector((s) => s.pricingWizard.merchantId);
  const merchant = useSelector((s) => s.pricingWizard.merchant);
  const [editMode, setEditMode] = useState(false);

  const handleChange = (name) => (e) => {
    dispatch(updateSignup({ [name]: e.target.value }));
  };

  return (
    <Box>
      {/* ── Merchant info card (read-only or editable) ── */}
      {merchantId ? (
        <Box>
          {!editMode ? (
            <Paper
              variant="outlined"
              sx={{ borderRadius: 2.5, overflow: 'hidden', borderColor: 'divider' }}
            >
              {/* card header */}
              <Box sx={{ px: 2.5, py: 1.75, bgcolor: (t) => alpha(t.palette.primary.main, 0.05), borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <BusinessIcon sx={{ fontSize: 19, color: '#fff' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: 15, lineHeight: 1.2 }}>
                        {signup.business_name || merchant?.legal || merchant?.brand || '—'}
                      </Typography>
                      {(() => {
                        const name = signup.brand || merchant?.brand;
                        const company = signup.business_name || merchant?.legal;
                        return name && name !== company ? (
                          <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500 }}>{name}</Typography>
                        ) : null;
                      })()}
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {merchant?.status && (
                      <Chip label={merchant.status} size="small" color="info" variant="outlined" sx={{ fontWeight: 700, fontSize: 10.5, height: 22 }} />
                    )}
                    <Button size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />} onClick={() => setEditMode(true)} sx={{ fontWeight: 600, fontSize: 12 }}>
                      Edit
                    </Button>
                  </Stack>
                </Stack>
              </Box>

              {/* card body */}
              <Box sx={{ px: 2.5, py: 2 }}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <InfoField icon={EmailOutlinedIcon} label="Email" value={signup.email || merchant?.email} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoField icon={PhoneOutlinedIcon} label="Contact Number" value={signup.contact || merchant?.contact_no} />
                  </Grid>
                  <Grid item xs={12}>
                    <InfoField
                      icon={LocationOnOutlinedIcon}
                      label="Registered Address"
                      value={
                        [signup.address, signup.city, signup.state, signup.pincode].filter(Boolean).join(', ') ||
                        [merchant?.address, merchant?.city, merchant?.state, merchant?.pincode].filter(Boolean).join(', ') ||
                        '—'
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          ) : (
            <Box>
              <Alert severity="warning" icon={<EditOutlinedIcon fontSize="small" />} sx={{ mb: 2, fontSize: 12 }}
                action={<Button size="small" color="inherit" onClick={() => setEditMode(false)}>Cancel</Button>}>
                You are editing merchant details. Changes will not update the backend — only local form data is affected at this stage.
              </Alert>
              <Grid container spacing={3}>
                {FIELDS.map((f) => {
                  const Icon = f.icon;
                  return (
                    <Grid item xs={12} md={6} key={f.name}>
                      <TextField
                        size="small" fullWidth
                        label={<span>{f.label}{f.required && <span style={{ color: '#FF4842' }}> *</span>}{f.optional && <span style={{ color: '#919EAB', marginLeft: 4, fontSize: 11 }}>(optional)</span>}</span>}
                        placeholder={f.placeholder}
                        type={f.type || 'text'}
                        value={signup[f.name] || ''}
                        onChange={(e) => dispatch(updateSignup({ [f.name]: e.target.value }))}
                        error={Boolean(errors[f.name])}
                        helperText={errors[f.name] || f.hint}
                        InputProps={{ startAdornment: <InputAdornment position="start"><Icon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
                      />
                    </Grid>
                  );
                })}
                <Grid item xs={12}>
                  <TextField size="small" fullWidth
                    label={<span>Billing / Registered Address <span style={{ color: '#FF4842' }}>*</span></span>}
                    placeholder="Door no., street, locality"
                    value={signup.address || ''}
                    onChange={(e) => dispatch(updateSignup({ address: e.target.value }))}
                    error={Boolean(errors.address)} helperText={errors.address}
                    InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField size="small" fullWidth label={<span>Pincode <span style={{ color: '#FF4842' }}>*</span></span>}
                    placeholder="6-digit pincode" value={signup.pincode || ''}
                    onChange={(e) => dispatch(updateSignup({ pincode: e.target.value }))}
                    error={Boolean(errors.pincode)} helperText={errors.pincode} inputProps={{ maxLength: 6 }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField size="small" fullWidth label={<span>City <span style={{ color: '#FF4842' }}>*</span></span>}
                    value={signup.city || ''} onChange={(e) => dispatch(updateSignup({ city: e.target.value }))}
                    error={Boolean(errors.city)} helperText={errors.city}
                    InputProps={{ startAdornment: <InputAdornment position="start"><LocationCityOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField size="small" fullWidth label={<span>State <span style={{ color: '#FF4842' }}>*</span></span>}
                    value={signup.state || ''} onChange={(e) => dispatch(updateSignup({ state: e.target.value }))}
                    error={Boolean(errors.state)} helperText={errors.state}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PublicOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      ) : (
        /* ── Editable form for new merchants ── */
        <WizardSection icon="📝" title="Merchant Sign-up" description="Registered business identity & primary contact.">
        <Grid container spacing={3}>
          {FIELDS.map((f) => {
            const Icon = f.icon;
            return (
              <Grid item xs={12} md={6} key={f.name}>
                <TextField
                  size="small"
                  fullWidth
                  label={
                    <span>
                      {f.label}
                      {f.required && <span style={{ color: '#FF4842' }}> *</span>}
                      {f.optional && <span style={{ color: '#919EAB', marginLeft: 4, fontSize: 11 }}>(optional)</span>}
                    </span>
                  }
                  placeholder={f.placeholder}
                  type={f.type || 'text'}
                  value={signup[f.name] || ''}
                  onChange={handleChange(f.name)}
                  error={Boolean(errors[f.name])}
                  helperText={errors[f.name] || f.hint}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            );
          })}
          <Grid item xs={12}>
            <TextField
              size="small" fullWidth
              label={<span>Billing / Registered Address <span style={{ color: '#FF4842' }}>*</span></span>}
              placeholder="Door no., street, locality"
              value={signup.address || ''}
              onChange={handleChange('address')}
              error={Boolean(errors.address)}
              helperText={errors.address}
              InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              size="small" fullWidth
              label={<span>Pincode <span style={{ color: '#FF4842' }}>*</span></span>}
              placeholder="6-digit pincode"
              value={signup.pincode || ''}
              onChange={handleChange('pincode')}
              error={Boolean(errors.pincode)}
              helperText={errors.pincode}
              inputProps={{ maxLength: 6 }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              size="small" fullWidth
              label={<span>City <span style={{ color: '#FF4842' }}>*</span></span>}
              value={signup.city || ''}
              onChange={handleChange('city')}
              error={Boolean(errors.city)}
              helperText={errors.city}
              InputProps={{ startAdornment: <InputAdornment position="start"><LocationCityOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              size="small" fullWidth
              label={<span>State <span style={{ color: '#FF4842' }}>*</span></span>}
              value={signup.state || ''}
              onChange={handleChange('state')}
              error={Boolean(errors.state)}
              helperText={errors.state}
              InputProps={{ startAdornment: <InputAdornment position="start"><PublicOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
            />
          </Grid>
        </Grid>
        </WizardSection>
      )}

      {/* v6 §4a — Merchant Master Info card */}
      <MasterInfoCard />

      {/* Payment & Credit — engagement model, AOV, COD remittance and the COD/prepaid
          split. Collected at sign-up (the FTR team holds this), not at Commercials.
          The recommended-credit-limit half still lives on the Commercials stage.
          Sits after Master Info because the AOV field defaults from it. */}
      <Box sx={{ mt: 2 }}>
        <CreditCard section="payment" />
      </Box>

      {/* Pickup / Warehouse — owned by the FTR team (Sign-up), since they hold
          whether the merchant uses Prozo warehouses or its own. */}
      <Box sx={{ mt: 2 }}>
        <WarehouseCard />
      </Box>
    </Box>
  );
}
